// English (Canada) dictionary
export const enCA = {
  app: {
    title: "FBO Micro UI App",
    theme: {
      light: "Light",
      dark: "Dark",
      system: "System",
      toggle: "Toggle theme",
    },
    settings: {
      open: "Open settings",
      title: "Application Settings",
      tabs: {
        auth: "Authentication",
        features: "Features",
        codes: "Code Values",
        debug: "Debug",
      },
    },
    api: {
      title: "API Configuration",
      url: "API URL:",
      environment: "Environment:",
      adminServiceApiEndpoint: "Admin Service API:",
    },
    auth: {
      title: "Authentication Status",
      status: "Status:",
      authenticated: "Authenticated",
      unauthenticated: "Unauthenticated",
      token: "Token:",
      source: "Source:",
      sourceUrl: "URL Parameter",
      sourceDefault: "Default Token",
      sourceCustom: "Custom Token",
      noTokenMessage: "No authentication token found. The application expects a token parameter in the URL.",
      clearCustomToken: "Clear custom token",
      confirmClearToken: "Are you sure you want to clear your custom token?",
      customToken: {
        title: "Custom Authentication Token",
        description: "Enter a custom token to use instead of the default token",
        placeholder: "Enter your JWT token here",
        save: "Save Token",
        paste: "Paste",
        success: "Token saved successfully!",
        error: "Please enter a valid token",
      },
    },
    apiExample: {
      title: "API Example",
      loading: "Loading...",
      callMainApi: "Call Main API",
      callAdminApi: "Call Admin API",
    },
    language: {
      switch: "Switch language",
      english: "English (Canada)",
      french: "Français (Canada)",
      current: "Current Language:",
      source: "Language Source:",
      sourceUrl: "URL Parameter",
      sourceCookie: "Cookie",
      sourceDefault: "Default",
    },
    navigation: {
      useTopNav: "Use Top Nav",
      useSideNav: "Use Side Nav",
    },
  },
  counter: {
    decrease: "Decrease",
    increase: "Increase",
  },
  featureFlags: {
    title: "Feature Flags",
    enabled: "Enabled",
    disabled: "Disabled",
    noFlags: "No feature flags available",
    mockData: "Mock Data",
    mockDataTitle: "Using Mock Data",
    mockDataDescription: "Feature flags are currently loaded from mock data due to CORS restrictions.",
    showFlags: "Show Feature Flags",
    hideFlags: "Hide Feature Flags",
    errors: {
      title: "Error",
      loadFailed: "Failed to load feature flags. Please try again later.",
      notAuthenticated: "Authentication required to load feature flags",
    },
  },
  codeValues: {
    title: "Code Values",
    noValues: "No code values available",
    showValues: "Show Code Values",
    hideValues: "Hide Code Values",
    errors: {
      title: "Error",
      loadFailed: "Failed to load code values. Please try again later.",
      notAuthenticated: "Authentication required to load code values",
    },
  },
  engagement: {
    title: "Engagement",
    setup: "Engagement Setup",
    setupProgress: "Setup progress",
    stepsCompleted: "steps completed",
    client: "Client",
    contact: "Contact",
    branch: "Branch",
    status: "Status",
    sections: {
      core: "Core Information",
      workers: "Worker setup",
      timesheet: "Timesheet and Expense setup",
      tasks: "Tasks",
      invoice: "Invoice setup",
      documents: "Documents",
    },
    core: {
      title: "Core",
      description: "Manage core engagement details",
      name: "Engagement Name",
      namePlaceholder: "Enter engagement name",
      code: "Engagement Code",
      codePlaceholder: "Enter engagement code",
      requestType: "Request Type",
      advisoryEngagement: "Advisory Engagement",
      internalEngagement: "Internal Engagement",
      engagementType: "Engagement Type",
      originalEngagement: "Original Engagement",
      changeRequestEngagement: "Change Request Engagement",
      engagementSource: "Original Engagement",
      selectOriginalEngagement: "Select original engagement",
      engagementSourceLocked: "Locked because other engagements list this one as their Original Engagement.",
      copyFromEngagement: "Copy from this engagement",
      copyConfirmation: "Loaded workers, rates, tasks, client, etc. Edit any field below before submitting.",
      copyNoStartDateTitle: "Start Date not set",
      copyNoStartDateMessage: "Copy sets each worker's Engagement Start Date to the later of two dates: their start date on the original engagement, and this engagement's Start Date. This engagement has no Start Date yet, so there is nothing to compare against and every worker will be copied with a blank start date. Cancel to set the Start Date first, then copy again.",
      copyNoStartDateProceed: "Copy anyway",
      copyNoStartDateCancel: "Cancel",
      startDate: "Start Date",
      endDate: "End Date",
      isPreEngagement: "Pre-engagement",
      isPreEngagementDescription: "Tag this engagement as scoping-phase work.",
      closedDate: "Project Completion Date",
      pickDate: "Pick a date",
      closedReason: "Closed Reason",
      selectReason: "Select a reason",
      internalCompany: "Internal Company",
      selectCompany: "Select company",
      branch: "Branch",
      selectBranch: "Select branch",
      salesPerson: "Sales Person",
      selectSalesPerson: "Select sales person",
      engagementLead: "Engagement Lead",
      selectEngagementLead: "Select engagement lead",
      currency: "Currency",
      selectCurrency: "Select currency",
      salesTax: "Sales Tax",
      selectProvinceState: "Select province/state",
      billingType: "Billing Type",
      timeBased: "Time-Based",
      milestoneBased: "Milestone-Based",
      totalAmount: "Contract Amount",
      enterTotalAmount: "Enter contract amount",
      totalInternalBudget: "Projected Cost",
      enterInternalBudget: "Enter projected cost",
      milestones: "Milestones",
      addMilestone: "Add Milestone",
      milestone: "Milestone",
      status: "Status",
      selectStatus: "Select status",
      milestoneReference: "Milestone Reference",
      amount: "Amount",
      internalBudget: "Internal Budget",
      generatingInvoice: "Generating invoice",
      generateInvoice: "Generate Invoice",
      regenerateInvoice: "Regenerate Invoice",
      remove: "Remove",
      cancel: "Cancel",
      checking: "Checking…",
      save: "Save",
      delete: "Delete",
      restore: "Restore",
      edit: "Edit",
      view: "View",
      download: "Download",
      uploading: "Uploading...",
      actions: "Actions",
      type: "Type",
      size: "Size",
      coreInformationComplete: "Core information complete",
    },
    clientInfo: {
      title: "Client Information",
      description: "Manage client details",
      clientSelection: "Client Selection",
      selectClient: "Select client",
      clientContact: "Client Contact",
      selectContact: "Select contact",
      clientInformationComplete: "Client information complete",
    },
    workers: {
      title: "Workers",
      description: "Manage workers assigned to the engagement",
      addWorker: "Add Worker",
      addWorkerDescription: "Add an existing worker to the engagement",
      workerName: "Worker Name",
      type: "Type",
      selectWorker: "Select worker",
      worker: "Worker",
      budgetedHoursPerDay: "Budgeted Hours per Day",
      billRate: "Bill Rate",
      reportingBillRate: "Reporting Bill Rate",
      payRate: "Pay Rate",
      payRates: "Pay Rates",
      rateName: "Rate Name",
      addRate: "Add a rate from the worker profile",
      removeRate: "Remove rate",
      billable: "Billable",
      nonBillable: "Non-billable",
      noRatesLeftToAdd: "Every rate on this worker's profile is already on this engagement.",
      rateCapReached: "A worker can be paid at up to {count} rates on one engagement.",
      reportingPayRate: "Reporting Pay Rate",
      paySchedule: "Pay Schedule",
      selectPaySchedule: "Select pay schedule",
      payScheduleChangeWarning:
        "Changes to pay schedule will impact future payments. Existing transactions will not be changed.",
      unit: "Unit",
      perHour: "Per Hour",
      perDay: "Per Day",
      perWeek: "Per Week",
      weekly: "Weekly",
      biWeekly: "Bi-Weekly",
      monthly: "Monthly",
      engagementStartDate: "Engagement Start Date",
      lastDayOfWork: "Last Day of Work",
      lastDayOfWorkHint: "Leave blank to align with the engagement's end date and any future changes to it.",
      lastDayOfWorkHoursConflict: "There are submitted timesheets with hours after this date. Choose a later Last Day of Work.",
      lastDayOfWorkTransactionConflict: "There are billing or payment transactions after this date. Choose a later Last Day of Work.",
      lastDayOfWorkCheckFailed: "Unable to validate this date right now. Please try again.",
      firstDayOfWorkHoursConflict: "There are submitted timesheets with hours before this date. Choose an earlier First Day of Work.",
      firstDayOfWorkTransactionConflict: "There are billing or payment transactions before this date. Choose an earlier First Day of Work.",
      firstDayOfWorkCheckFailed: "Unable to validate this date right now. Please try again.",
      removed: "Removed",
      new: "New",
      terminated: "Terminated",
      complete: "Complete",
      setLastDay: "Set Last Day of Work",
      setLastDayDescription: "When will this worker stop working on the engagement?",
      removeWorker: "Remove Worker",
      scheduleChange: "Schedule Change",
      scheduledChangesTitle: "Scheduled Budgeted Hours Changes",
      scheduledChangesDescription: "Each change sets the worker's budgeted hours per day from its effective date. Past changes are in the engagement history.",
      currentBudgetedHours: "Current",
      effectiveDate: "Effective Date",
      hoursPerDay: "Hours per Day",
      noScheduledChanges: "No scheduled changes.",
      cancelChange: "Cancel change",
      scheduled: "Scheduled",
      seeScheduledChanges: "See scheduled changes",
      hoursPerDayFrom: "{hours} h/day from {date}",
      hoursPerDayValue: "{hours} h/day",
      afterLastDayOfWork: "after last day of work",
      goBack: "Go Back",
      save: "Save",
      done: "Done",
      removalCancelsChangesTitle: "Cancel scheduled hours changes?",
      removalCancelsChangesOne: "This worker has a scheduled change to budgeted hours per day. Removing the worker cancels it.",
      removalCancelsChangesMany: "This worker has {count} scheduled changes to budgeted hours per day. Removing the worker cancels them.",
      changesAfterLastDayTitle: "Scheduled hours changes after the last day",
      changesAfterLastDayOne: "1 scheduled change to budgeted hours per day falls after this Last Day of Work. It won't take effect unless the Last Day of Work is extended.",
      changesAfterLastDayMany: "{count} scheduled changes to budgeted hours per day fall after this Last Day of Work. They won't take effect unless the Last Day of Work is extended.",
      changesNowDueTitle: "Scheduled hours changes now due",
      changesNowDueOne: "1 scheduled change to budgeted hours per day is dated today or earlier and will apply tonight. It sets the hours to {hours} from {date}. Cancel it before saving if it is no longer wanted.",
      changesNowDueMany: "{count} scheduled changes to budgeted hours per day are dated today or earlier and will apply tonight. The latest sets the hours to {hours} from {date}. Cancel any you no longer want before saving.",
      editWorker: "Edit Worker",
      editWorkerDescription: "Update the worker details",
      updateWorker: "Update Worker",
      profileFieldRequired: "This field must be set on the worker profile. Please complete the profile before continuing.",
      jobTitle: "Job Title",
      jobTitlePlaceholder: "Enter job title",
      jobCategory: "Job Category",
      selectJobCategory: "Select job category",
      jobFunction: "Job Function",
      selectJobFunction: "Select job function",
      selectJobCategoryFirst: "Select job category first",
      noWorkersAdded: "No workers have been added yet.",
      workersInformationComplete: "Workers information complete",
      extensionNewStart: "New Start",
      extensionNewEnd: "New End",
    },
    timesheet: {
      title: "Timesheet and Expenses",
      description: "Manage timesheet and expense settings",
      timesheetCycle: "Timesheet Cycle",
      selectTimesheetCycle: "Select timesheet cycle",
      timesheetApprovers: "Timesheet Approvers",
      selectApprovers: "Select approvers",
      approvalSequence: "Approval Sequence",
      selectApprovalSequence: "Select approval sequence",
      timesheetInformationComplete: "Timesheet information complete",
    },
    expense: {
      areExpensesEnabled: "Are expenses enabled?",
      internalApprover: "Internal Approver",
    },
    tasks: {
      title: "Tasks",
      description: "Manage tasks associated with the engagement",
      addTask: "Add Task",
      addTaskDescription: "Add a new task to the engagement",
      taskName: "Task Name",
      enterTaskName: "Enter task name",
      billable: "Billable",
      nonBillable: "Non-Billable",
      noTasksAdded: "No tasks have been added yet.",
      tasksInformationComplete: "Tasks information complete",
    },
    invoice: {
      title: "Invoice",
      description: "Manage invoice settings",
      billingFrequency: "Billing Frequency",
      selectBillingFrequency: "Select billing frequency",
      uponMilestoneCompletion: "Upon Milestone Completion",
      paymentTerms: "Payment Terms",
      selectPaymentTerms: "Select payment terms",
      invoiceTemplate: "Invoice Template",
      selectInvoiceTemplate: "Select invoice template",
      invoiceRecipient: "Invoice Recipient",
      selectInvoiceRecipient: "Select invoice recipient",
      deliveryMethod: "Delivery Method",
      selectDeliveryMethod: "Select delivery method",
      includeTimesheet: "Include Timesheet",
      invoiceInformationComplete: "Invoice information complete",
    },
    documents: {
      title: "Documents",
      description: "Manage documents related to the engagement",
      uploadDocument: "Upload Document",
      uploadDocumentDescription: "Upload a new document to the engagement",
      documentName: "Document Name",
      enterDocumentName: "Enter document name",
      clickToUpload: "Click to upload",
      orDragAndDrop: "or drag and drop",
      selectedFile: "Selected file",
      removeFile: "Remove File",
      upload: "Upload",
      uploadedBy: "Uploaded By",
      dateUploaded: "Date Uploaded",
      noDocumentsUploaded: "No documents have been uploaded yet.",
      documentsInformationComplete: "Documents information complete",
      uploadSuccess: "Upload Successful",
      uploadSuccessDescription: "Document has been uploaded successfully.",
      uploadError: "Upload Failed",
      uploadErrorDescription: "Failed to upload document",
      fetchError: "Failed to Load Documents",
      fetchErrorDescription: "Could not load existing documents",
    },
    actions: {
      save: "Save",
      submit: "Submit",
      submitForReview: "Submit for Review",
      approve: "Approve",
      decline: "Decline",
      discard: "Discard",
      recall: "Recall",
      recallToAccounting: "Recall to Accounting",
      complete: "Mark as Complete",
      reactivate: "Move back to Active",
      createChangeRequest: "Edit",
      refresh: "Cancel Changes",
      discardRevertBase: "Cancel Edit",
      createVersion: "Edit",
      approveReplaceBase: "Replace",
      terminateWorker: "Terminate Worker",
      applyScheduledBudgetedHours: "Apply Scheduled Hours",
    },
    errors: {
      cannotOpen: {
        title: "Can't open this engagement",
        body: "It may not exist, or you may not have permission to view it.",
      },
      loadFailed: {
        title: "Failed to load engagement",
        tryAgain: "Try Again",
      },
    },
  },
  resourceAvailability: {
    page: {
      title: "Resource availability",
      subtitle: "Real-time resource availability tracking",
      export: "Export",
      loading: "Loading resource availability…",
      heatmapTitleAllocated: "Allocation heatmap",
      heatmapTitleAvailable: "Availability heatmap",
      utilizationLinkPrefix: "Looking for utilization trends?",
      utilizationLinkText: "See the engagement dashboard's Utilization widget",
      utilizationLinkSuffix: "for weekly billable / utilized % by worker type.",
    },
    toggles: {
      allocated: "Allocated %",
      available: "Available %",
      granularityMonthly: "Monthly",
      granularityWeekly: "Weekly",
      granularityAria: "Heatmap granularity",
    },
    kpi: {
      teamAvgAllocated: "Team avg allocated",
      teamAvgAvailable: "Team avg available",
      workerSingular: "worker",
      workerPlural: "workers",
      endingInRange: "Engagements ending",
      availableHeadcount: "Available headcount*",
      upFromToday: "Up from {n} today",
      downFromToday: "Down from {n} today",
      sameAsToday: "Same as today ({n})",
      thresholdPrefix: "* workers with ≥",
      thresholdSuffix: "% availability at any point in range",
      decreaseThreshold: "Decrease availability threshold",
      increaseThreshold: "Increase availability threshold",
    },
    filterBar: {
      searchPlaceholder: "Search worker",
      searchAriaLabel: "Filter workers by name",
      clearNameFilter: "Clear name filter",
      workerType: "Worker Type",
      jobFunction: "Job Function",
      skills: "Skills",
      certifications: "Certifications",
      proficiencyLevels: "Proficiency Level",
      filterByLabel: "Filter by {label}",
      clearAll: "Clear all",
      clear: "Clear",
      emptyWorkerTypes: "No worker types in current data",
      emptyJobFunctions: "No job functions in current data",
      emptySkills: "No skills in current data",
      emptyCertifications: "No certifications in current data",
      emptyProficiencyLevels: "No proficiency levels in current data",
      chipName: "Name",
      chipWorkerType: "Worker Type",
      chipJobFunction: "Job Function",
      chipSkill: "Skill",
      chipCertification: "Certification",
      removeFilterAria: "Remove {label} filter",
    },
    filterPane: {
      title: "Filters",
      description: "Filter the dashboard by worker attributes. Filters apply immediately.",
      triggerLabel: "Filters",
      activeCountAria: "{n} active filters",
      resetAll: "Reset all",
      searchSection: "Search",
      workerTypeSection: "Worker Type",
      jobFunctionSection: "Job Function",
      skillsSection: "Skills",
      certificationsSection: "Certifications",
      proficiencyLevelsSection: "Proficiency Level",
      branchSection: "Branch",
      workerLocationSection: "Worker location",
      procurementSourceSection: "Procurement source",
      functionalManagerSection: "Functional manager",
      engagementSection: "Engagement",
      emptyBranches: "No branches in current data",
      emptyWorkerLocations: "No worker locations in current data",
      emptyProcurementSources: "No procurement sources in current data",
      emptyFunctionalManagers: "No functional managers in current data",
      emptyEngagements: "No engagements in current data",
      clearSection: "Clear",
      sectionSearchPlaceholder: "Search {title}",
      sectionSearchAria: "Search {title} options",
      clearSectionSearchAria: "Clear search",
      sectionNoMatches: "No matches",
      activeFiltersLabel: "Active filters",
      clearAllPills: "Clear all",
      removePillAria: "Remove {label} filter",
    },
    heatmap: {
      workerHeader: "Worker",
      nowLabel: "NOW",
      emptyState: "No workers match the current filters.",
      activeEngagementSubline: "{n} active engagement",
      activeEngagementSublinePlural: "{n} active engagements",
      workerRoleAndActive: "{role} · {n} active",
      moreSuffix: "+ {n} more",
      viewDetailAria: "View availability detail for {name}",
      overTooltip: "Single-day budgeted hours exceeds capacity in this month",
      overLabel: "over",
      teamSelectionAria: "Add {name} to team timeline",
      selectAllAria: "Select all workers",
      viewRangeFromLabel: "From",
      viewRangeToLabel: "To",
      viewRangeFromAria: "Filter heatmap from date",
      viewRangeToAria: "Filter heatmap to date",
      pendingTooltip: "Loading…",
      timeOffHeading: "Time off",
      timeOffPtoLabel: "PTO",
      timeOffStatLabel: "Stat holiday",
      timeOffHalfDaySuffix: "½ day",
      timeOffIconAria: "Time off in this period",
      endsOnLabel: "Ends {date}",
      endedOnLabel: "Ended {date}",
      endBucketTooltip: "End date: {date}",
      afterEndTooltip: "Past the worker’s end date ({date}) — no capacity to plan against",
      assignedPastEndTooltip: "Assigned work past the worker’s end date ({date}) — extend the profile or move the work",
      expiredCertificationBadge: "Certification expired",
      infoTitle: "How this heatmap works",
      allocationLabel: "Allocation",
      availabilityLabel: "Availability",
      infoAllocated: "Planned engagement hours ÷ capacity.",
      infoAvailable: "(Capacity − planned hours − PTO − stat holidays) ÷ capacity.",
      infoPtoBlind: "Allocation ignores PTO & stat holidays, so allocation and availability won’t add up to 100%.",
      infoCapacity: "Capacity counts weekdays only (Mon–Fri); weekends never count.",
      infoWeekly: "A week’s allocation and availability are computed across the whole week. So an engagement at 8 hours per day may read 100% allocated for a week it fully covers, but less than 100% for its first week if it started mid-week.",
      infoPalm: "This palm-tree marker flags PTO or stat holidays in a cell. Hover over it for the exact dates.",
      infoEndDate: "This flag marks the worker’s end date: their termination date if a termination is pending, otherwise their profile end date. Cells after it are greyed out. A red percentage on a greyed cell means work is already assigned past that date. Salaried workers have no profile end date, so their rows carry no flag unless a termination is pending.",
    },
    monthFilter: {
      filterAllocSubtitle: "Filter by allocation %",
      filterAvailSubtitle: "Filter by availability %",
      filterAllocAriaLabel: "Filter {month} allocation",
      filterAvailAriaLabel: "Filter {month} availability",
      opAbove: "≥ above",
      opBelow: "≤ below",
      valuePlaceholder: "e.g. 75",
      apply: "Apply",
      clear: "Clear",
    },
    workerActions: {
      triggerAriaLabel: "Actions for {name}",
      showBreakdown: "Show engagement breakdown",
      openProfileNewTab: "Open user profile in new tab",
    },
    drilldown: {
      backToTeam: "Back to team",
      loading: "Loading…",
      notFound: "Worker not found in current window.",
      engagementSingular: "engagement",
      engagementPlural: "engagements",
      currentAllocated: "Current allocated",
      currentSubtext: "This month (actual)",
      avgForecastAlloc: "Avg forecast alloc",
      avgSubtext: "Next 12 months",
      fullAvailabilityStarts: "Full availability starts",
      nowLabel: "Now",
      fullAvailabilityNoEng: "No active engagements",
      fullAvailabilitySubtext: "After all current engagements end",
      plannedPto: "Planned PTO",
      plannedPtoDaysSingular: "{n} day over 12 months",
      plannedPtoDaysPlural: "{n} days over 12 months",
      expiredCertification: "Expired",
      engagementTimeline: "Engagement timeline",
      timelineEmpty: "No active engagements",
      breakdownTitle: "Allocation breakdown — 12-month forecast",
      monthlyForecast: "Monthly forecast",
      ptoUnavailable: "PTO data unavailable — planned PTO is not shown.",
    },
    monthlyTable: {
      month: "Month",
      capacity: "Capacity",
      allocated: "Allocated",
      stat: "Stat",
      pto: "PTO",
      allocPct: "Alloc %",
      availPct: "Avail %",
    },
    timeline: {
      engagementHeader: "Engagement",
      endingSoonBadge: "ending soon",
      monthSingular: "1 mo",
      monthPlural: "{n} mos",
      workerActiveLegend: "Worker active",
      engagementWindowLegend: "Engagement window",
      colorsVaryNote: "colors vary per worker",
    },
    team: {
      fullyAvailable: "Fully available",
      engagementSingular: "engagement",
      engagementPlural: "engagements",
      removeWorkerAria: "Remove {name} from team timeline",
      openEngagementAria: "Open engagement {name}",
      teamTimelineHeadingSingular: "Team timeline · 1 worker",
      teamTimelineHeadingPlural: "Team timeline · {n} workers",
      clearSelection: "Clear selection",
      viewRangeFromAria: "Filter team timeline from date",
      viewRangeToAria: "Filter team timeline to date",
    },
    breakdown: {
      allocated: "Allocated",
      available: "Available",
      pto: "PTO",
      statHoliday: "Stat holiday",
    },
  },
}

// French (Canada) dictionary
export const frCA = {
  app: {
    title: "Application Micro UI FBO",
    theme: {
      light: "Clair",
      dark: "Sombre",
      system: "Système",
      toggle: "Changer le thème",
    },
    settings: {
      open: "Ouvrir les paramètres",
      title: "Paramètres de l'Application",
      tabs: {
        auth: "Authentification",
        features: "Fonctionnalités",
        codes: "Valeurs de Code",
        debug: "Débogage",
      },
    },
    api: {
      title: "Configuration de l'API",
      url: "URL de l'API :",
      environment: "Environnement :",
      adminServiceApiEndpoint: "API de Service Admin :",
    },
    auth: {
      title: "État d'Authentification",
      status: "Statut :",
      authenticated: "Authentifié",
      unauthenticated: "Non Authentifié",
      token: "Jeton :",
      source: "Source :",
      sourceUrl: "Paramètre d'URL",
      sourceDefault: "Jeton par Défaut",
      sourceCustom: "Jeton Personnalisé",
      noTokenMessage: "Aucun jeton d'authentification trouvé. L'application attend un paramètre de jeton dans l'URL.",
      clearCustomToken: "Effacer le jeton personnalisé",
      confirmClearToken: "Êtes-vous sûr de vouloir effacer votre jeton personnalisé ?",
      customToken: {
        title: "Jeton d'Authentification Personnalisé",
        description: "Entrez un jeton personnalisé à utiliser au lieu du jeton par défaut",
        placeholder: "Entrez votre jeton JWT ici",
        save: "Enregistrer le Jeton",
        paste: "Coller",
        success: "Jeton enregistré avec succès !",
        error: "Veuillez entrer un jeton valide",
      },
    },
    apiExample: {
      title: "Exemple d'API",
      loading: "Chargement...",
      callMainApi: "Appeler l'API Principale",
      callAdminApi: "Appeler l'API Admin",
    },
    language: {
      switch: "Changer de langue",
      english: "English (Canada)",
      french: "Français (Canada)",
      current: "Langue Actuelle :",
      source: "Source de la Langue :",
      sourceUrl: "Paramètre d'URL",
      sourceCookie: "Cookie",
      sourceDefault: "Par Défaut",
    },
    navigation: {
      useTopNav: "Utiliser la Nav. Haute",
      useSideNav: "Utiliser la Nav. Latérale",
    },
  },
  counter: {
    decrease: "Diminuer",
    increase: "Augmenter",
  },
  featureFlags: {
    title: "Indicateurs de Fonctionnalités",
    enabled: "Activé",
    disabled: "Désactivé",
    noFlags: "Aucun indicateur de fonctionnalité disponible",
    mockData: "Données Simulées",
    mockDataTitle: "Utilisation de Données Simulées",
    mockDataDescription:
      "Les indicateurs de fonctionnalités sont actuellement chargés à partir de données simulées en raison des restrictions CORS.",
    showFlags: "Afficher les Indicateurs",
    hideFlags: "Masquer les Indicateurs",
    errors: {
      title: "Erreur",
      loadFailed: "Échec du chargement des indicateurs de fonctionnalités. Veuillez réessayer plus tard.",
      notAuthenticated: "Authentification requise pour charger les indicateurs de fonctionnalités",
    },
  },
  codeValues: {
    title: "Valeurs de Code",
    noValues: "Aucune valeur de code disponible",
    showValues: "Afficher les Valeurs de Code",
    hideValues: "Masquer les Valeurs de Code",
    errors: {
      title: "Erreur",
      loadFailed: "Échec du chargement des valeurs de code. Veuillez réessayer plus tard.",
      notAuthenticated: "Authentification requise pour charger les valeurs de code",
    },
  },
  engagement: {
    title: "Engagement",
    setup: "Configuration d'Engagement",
    setupProgress: "Progrès de la Configuration d'Engagement",
    stepsCompleted: "étapes terminées",
    client: "Client",
    contact: "Contact",
    branch: "Succursale",
    status: "Statut",
    sections: {
      core: "Informations principales",
      workers: "Configuration des travailleurs",
      timesheet: "Configuration de feuille de temps et de dépenses",
      tasks: "Tâches",
      invoice: "Configuration de facture",
      documents: "Documents",
    },
    core: {
      title: "Principal",
      description: "Gérer les détails principaux de l'engagement",
      name: "Nom de l'engagement",
      namePlaceholder: "Entrez le nom de l'engagement",
      code: "Code de l'engagement",
      codePlaceholder: "Entrez le code de l'engagement",
      requestType: "Type de Requête",
      advisoryEngagement: "Engagement Consultatif",
      internalEngagement: "Engagement Interne",
      engagementType: "Type d'Engagement",
      originalEngagement: "Engagement Original",
      changeRequestEngagement: "Engagement de Demande de Changement",
      engagementSource: "Engagement Original",
      selectOriginalEngagement: "Sélectionner l'engagement original",
      engagementSourceLocked: "Verrouillé car d'autres engagements désignent celui-ci comme leur Engagement Original.",
      copyFromEngagement: "Copier depuis cet engagement",
      copyConfirmation: "Travailleurs, taux, tâches, client, etc. chargés. Modifiez les champs ci-dessous avant de soumettre.",
      copyNoStartDateTitle: "Date de début non renseignée",
      copyNoStartDateMessage: "La copie fixe la date de début d'engagement de chaque travailleur à la plus tardive de deux dates : sa date de début sur l'engagement original, et la date de début de cet engagement. Cet engagement n'a pas encore de date de début, il n'y a donc rien à comparer et chaque travailleur sera copié sans date de début. Annulez pour renseigner d'abord la date de début, puis copiez de nouveau.",
      copyNoStartDateProceed: "Copier quand même",
      copyNoStartDateCancel: "Annuler",
      startDate: "Date de Début",
      endDate: "Date de Fin",
      isPreEngagement: "Pré-engagement",
      isPreEngagementDescription: "Marquer cet engagement comme travail en phase de cadrage.",
      closedDate: "Date d'achèvement du projet",
      pickDate: "Choisir une date",
      closedReason: "Raison de Fermeture",
      selectReason: "Sélectionner une raison",
      internalCompany: "Entreprise Interne",
      selectCompany: "Sélectionner l'entreprise",
      branch: "Succursale",
      selectBranch: "Sélectionner la succursale",
      salesPerson: "Représentant des Ventes",
      selectSalesPerson: "Sélectionner le représentant des ventes",
      engagementLead: "Responsable d'Engagement",
      selectEngagementLead: "Sélectionner le responsable d'engagement",
      currency: "Devise",
      selectCurrency: "Sélectionner la devise",
      salesTax: "Taxe de Vente",
      selectProvinceState: "Sélectionner la province/état",
      billingType: "Type de Facturation",
      timeBased: "Basé sur le Temps",
      milestoneBased: "Basé sur les Jalons",
      totalAmount: "Montant du contrat",
      enterTotalAmount: "Entrez le montant du contrat",
      totalInternalBudget: "Coût projeté",
      enterInternalBudget: "Entrez le coût projeté",
      milestones: "Jalons",
      addMilestone: "Ajouter un Jalon",
      milestone: "Jalon",
      status: "Statut",
      selectStatus: "Sélectionner le statut",
      milestoneReference: "Référence du Jalon",
      amount: "Montant",
      internalBudget: "Budget Interne",
      generatingInvoice: "Génération de facture",
      generateInvoice: "Générer la Facture",
      regenerateInvoice: "Régénérer la Facture",
      remove: "Supprimer",
      cancel: "Annuler",
      checking: "Vérification…",
      save: "Enregistrer",
      delete: "Supprimer",
      restore: "Restaurer",
      edit: "Modifier",
      view: "Voir",
      download: "Télécharger",
      uploading: "Téléchargement...",
      actions: "Actions",
      type: "Type",
      size: "Taille",
      coreInformationComplete: "Informations principales terminées",
    },
    clientInfo: {
      title: "Informations Client",
      description: "Gérer les détails du client",
      clientSelection: "Sélection du Client",
      selectClient: "Sélectionner le client",
      clientContact: "Contact Client",
      selectContact: "Sélectionner le contact",
      clientInformationComplete: "Informations client terminées",
    },
    workers: {
      title: "Travailleurs",
      description: "Gérer les travailleurs assignés à l'engagement",
      addWorker: "Ajouter un Travailleur",
      addWorkerDescription: "Ajouter un travailleur existant à l'engagement",
      workerName: "Nom du Travailleur",
      type: "Type",
      selectWorker: "Sélectionner le travailleur",
      worker: "Travailleur",
      budgetedHoursPerDay: "Heures Budgétées par Jour",
      billRate: "Taux de Facturation",
      reportingBillRate: "Taux de Facturation Déclaré",
      payRate: "Taux de Paiement",
      payRates: "Taux de Paiement",
      rateName: "Nom du Taux",
      addRate: "Ajouter un taux du profil du travailleur",
      removeRate: "Retirer le taux",
      billable: "Facturable",
      nonBillable: "Non facturable",
      noRatesLeftToAdd: "Chaque taux du profil de ce travailleur est déjà sur cet engagement.",
      rateCapReached: "Un travailleur peut être payé jusqu'à {count} taux sur un engagement.",
      reportingPayRate: "Taux de Paiement Déclaré",
      paySchedule: "Calendrier de Paiement",
      selectPaySchedule: "Sélectionner le calendrier de paiement",
      payScheduleChangeWarning:
        "Les modifications apportées au calendrier de paiement auront une incidence sur les paiements futurs. Les transactions existantes ne seront pas modifiées.",
      unit: "Unité",
      perHour: "Par Heure",
      perDay: "Par Jour",
      perWeek: "Par Semaine",
      weekly: "Hebdomadaire",
      biWeekly: "Bi-Hebdomadaire",
      monthly: "Mensuel",
      engagementStartDate: "Date de Début d'Engagement",
      lastDayOfWork: "Dernier Jour de Travail",
      lastDayOfWorkHint: "Laisser vide pour s'aligner sur la date de fin de l'engagement et ses modifications futures.",
      lastDayOfWorkHoursConflict: "Des feuilles de temps soumises comportent des heures après cette date. Choisissez un dernier jour de travail ultérieur.",
      lastDayOfWorkTransactionConflict: "Des transactions de facturation ou de paiement existent après cette date. Choisissez un dernier jour de travail ultérieur.",
      lastDayOfWorkCheckFailed: "Impossible de valider cette date pour le moment. Veuillez réessayer.",
      firstDayOfWorkHoursConflict: "Des feuilles de temps soumises comportent des heures avant cette date. Choisissez un premier jour de travail antérieur.",
      firstDayOfWorkTransactionConflict: "Des transactions de facturation ou de paiement existent avant cette date. Choisissez un premier jour de travail antérieur.",
      firstDayOfWorkCheckFailed: "Impossible de valider cette date pour le moment. Veuillez réessayer.",
      removed: "Retiré",
      new: "Nouveau",
      terminated: "Terminé",
      complete: "Complété",
      setLastDay: "Définir le Dernier Jour de Travail",
      setLastDayDescription: "Quand ce travailleur cessera-t-il de travailler sur l'engagement ?",
      removeWorker: "Retirer le Travailleur",
      scheduleChange: "Planifier un Changement",
      scheduledChangesTitle: "Changements Planifiés des Heures Budgétées",
      scheduledChangesDescription: "Chaque changement fixe les heures budgétées par jour du travailleur à partir de sa date d'effet. Les changements passés figurent dans l'historique de l'engagement.",
      currentBudgetedHours: "Actuel",
      effectiveDate: "Date d'Effet",
      hoursPerDay: "Heures par Jour",
      noScheduledChanges: "Aucun changement planifié.",
      cancelChange: "Annuler le changement",
      scheduled: "Planifié",
      seeScheduledChanges: "Voir les changements planifiés",
      hoursPerDayFrom: "{hours} h/jour à partir du {date}",
      hoursPerDayValue: "{hours} h/jour",
      afterLastDayOfWork: "après le dernier jour de travail",
      goBack: "Retour",
      save: "Enregistrer",
      done: "Terminé",
      removalCancelsChangesTitle: "Annuler les changements d'heures planifiés ?",
      removalCancelsChangesOne: "Ce travailleur a un changement planifié des heures budgétées par jour. Retirer le travailleur l'annule.",
      removalCancelsChangesMany: "Ce travailleur a {count} changements planifiés des heures budgétées par jour. Retirer le travailleur les annule.",
      changesAfterLastDayTitle: "Changements d'heures planifiés après le dernier jour",
      changesAfterLastDayOne: "1 changement planifié des heures budgétées par jour tombe après ce dernier jour de travail. Il ne prendra pas effet à moins que le dernier jour de travail soit prolongé.",
      changesAfterLastDayMany: "{count} changements planifiés des heures budgétées par jour tombent après ce dernier jour de travail. Ils ne prendront pas effet à moins que le dernier jour de travail soit prolongé.",
      changesNowDueTitle: "Changements d'heures planifiés maintenant dus",
      changesNowDueOne: "1 changement planifié des heures budgétées par jour est daté d'aujourd'hui ou d'avant et s'appliquera cette nuit. Il fixe les heures à {hours} à partir du {date}. Annulez-le avant d'enregistrer s'il n'est plus voulu.",
      changesNowDueMany: "{count} changements planifiés des heures budgétées par jour sont datés d'aujourd'hui ou d'avant et s'appliqueront cette nuit. Le plus récent fixe les heures à {hours} à partir du {date}. Annulez ceux dont vous ne voulez plus avant d'enregistrer.",
      editWorker: "Modifier le Travailleur",
      editWorkerDescription: "Mettre à jour les détails du travailleur",
      updateWorker: "Mettre à Jour le Travailleur",
      profileFieldRequired: "Ce champ doit être défini sur le profil du travailleur. Veuillez compléter le profil avant de continuer.",
      jobTitle: "Titre du Poste",
      jobTitlePlaceholder: "Entrez le titre du poste",
      jobCategory: "Catégorie d'Emploi",
      selectJobCategory: "Sélectionner la catégorie d'emploi",
      jobFunction: "Fonction d'Emploi",
      selectJobFunction: "Sélectionner la fonction d'emploi",
      selectJobCategoryFirst: "Sélectionner d'abord la catégorie d'emploi",
      noWorkersAdded: "Aucun travailleur n'a encore été ajouté.",
      workersInformationComplete: "Informations des travailleurs terminées",
      extensionNewStart: "Nouveau début",
      extensionNewEnd: "Nouvelle fin",
    },
    timesheet: {
      title: "Feuille de Temps",
      description: "Gérer les paramètres de feuille de temps et de dépenses",
      timesheetCycle: "Cycle de Feuille de Temps",
      selectTimesheetCycle: "Sélectionner le cycle de feuille de temps",
      timesheetApprovers: "Approbateurs de Feuille de Temps",
      selectApprovers: "Sélectionner les approbateurs",
      approvalSequence: "Séquence d'Approbation",
      selectApprovalSequence: "Sélectionner la séquence d'approbation",
      timesheetInformationComplete: "Informations de feuille de temps terminées",
    },
    expense: {
      areExpensesEnabled: "Les dépenses sont-elles activées ?",
      internalApprover: "Approbateur Interne",
    },
    tasks: {
      title: "Tâches",
      description: "Gérer les tâches associées à l'engagement",
      addTask: "Ajouter une Tâche",
      addTaskDescription: "Ajouter une nouvelle tâche à l'engagement",
      taskName: "Nom de la Tâche",
      enterTaskName: "Entrez le nom de la tâche",
      billable: "Facturable",
      nonBillable: "Non-Facturable",
      noTasksAdded: "Aucune tâche n'a encore été ajoutée.",
      tasksInformationComplete: "Informations des tâches terminées",
    },
    invoice: {
      title: "Facture",
      description: "Gérer les paramètres de facturation",
      billingFrequency: "Fréquence de Facturation",
      selectBillingFrequency: "Sélectionner la fréquence de facturation",
      uponMilestoneCompletion: "À la Fin du Jalon",
      paymentTerms: "Conditions de Paiement",
      selectPaymentTerms: "Sélectionner les conditions de paiement",
      invoiceTemplate: "Modèle de Facture",
      selectInvoiceTemplate: "Sélectionner le modèle de facture",
      invoiceRecipient: "Destinataire de la Facture",
      selectInvoiceRecipient: "Sélectionner le destinataire de la facture",
      deliveryMethod: "Méthode de Livraison",
      selectDeliveryMethod: "Sélectionner la méthode de livraison",
      includeTimesheet: "Inclure la Feuille de Temps",
      invoiceInformationComplete: "Informations de facturation terminées",
    },
    documents: {
      title: "Documents",
      description: "Gérer les documents liés à l'engagement",
      uploadDocument: "Télécharger un Document",
      uploadDocumentDescription: "Télécharger un nouveau document vers l'engagement",
      documentName: "Nom du Document",
      enterDocumentName: "Entrez le nom du document",
      clickToUpload: "Cliquez pour télécharger",
      orDragAndDrop: "ou glisser-déposer",
      selectedFile: "Fichier sélectionné",
      removeFile: "Supprimer le Fichier",
      upload: "Télécharger",
      uploadedBy: "Téléchargé par",
      dateUploaded: "Date de Téléchargement",
      noDocumentsUploaded: "Aucun document n'a encore été téléchargé.",
      documentsInformationComplete: "Informations des documents terminées",
      uploadSuccess: "Téléchargement Réussi",
      uploadSuccessDescription: "Le document a été téléchargé avec succès.",
      uploadError: "Échec du Téléchargement",
      uploadErrorDescription: "Échec du téléchargement du document",
      fetchError: "Échec du Chargement des Documents",
      fetchErrorDescription: "Impossible de charger les documents existants",
    },
    actions: {
      save: "Enregistrer",
      submit: "Soumettre",
      submitForReview: "Soumettre pour révision",
      approve: "Approuver",
      decline: "Refuser",
      discard: "Jeter",
      recall: "Rappeler",
      recallToAccounting: "Rappeler à la comptabilité",
      complete: "Marquer comme terminé",
      reactivate: "Remettre à Actif",
      createChangeRequest: "Modifier",
      refresh: "Annuler les modifications",
      discardRevertBase: "Annuler la modification",
      createVersion: "Modifier",
      approveReplaceBase: "Remplacer",
      terminateWorker: "Terminer le travailleur",
      applyScheduledBudgetedHours: "Appliquer les heures planifiées",
    },
    errors: {
      cannotOpen: {
        title: "Impossible d'ouvrir cet engagement",
        body: "Il n'existe peut-être pas, ou vous n'avez peut-être pas la permission de le consulter.",
      },
      loadFailed: {
        title: "Échec du chargement de l'engagement",
        tryAgain: "Réessayer",
      },
    },
  },
  resourceAvailability: {
    page: {
      title: "Disponibilité des ressources",
      subtitle: "Suivi en temps réel de la disponibilité des ressources",
      export: "Exporter",
      loading: "Chargement de la disponibilité des ressources…",
      heatmapTitleAllocated: "Carte d'allocation",
      heatmapTitleAvailable: "Carte de disponibilité",
      utilizationLinkPrefix: "Vous cherchez les tendances d'utilisation ?",
      utilizationLinkText: "Consultez le widget Utilisation du tableau de bord des engagements",
      utilizationLinkSuffix: "pour le % facturable / utilisé hebdomadaire par type de travailleur.",
    },
    toggles: {
      allocated: "% alloué",
      available: "% disponible",
      granularityMonthly: "Mensuel",
      granularityWeekly: "Hebdomadaire",
      granularityAria: "Granularité de la carte",
    },
    kpi: {
      teamAvgAllocated: "Allocation moyenne de l'équipe",
      teamAvgAvailable: "Disponibilité moyenne de l'équipe",
      workerSingular: "travailleur",
      workerPlural: "travailleurs",
      endingInRange: "Engagements en fin de course",
      availableHeadcount: "Effectifs disponibles*",
      upFromToday: "En hausse, contre {n} aujourd'hui",
      downFromToday: "En baisse, contre {n} aujourd'hui",
      sameAsToday: "Identique à aujourd'hui ({n})",
      thresholdPrefix: "* travailleurs avec ≥",
      thresholdSuffix: "% de disponibilité à un moment donné de la période",
      decreaseThreshold: "Diminuer le seuil de disponibilité",
      increaseThreshold: "Augmenter le seuil de disponibilité",
    },
    filterBar: {
      searchPlaceholder: "Rechercher un travailleur",
      searchAriaLabel: "Filtrer les travailleurs par nom",
      clearNameFilter: "Effacer le filtre de nom",
      workerType: "Type de travailleur",
      jobFunction: "Fonction",
      skills: "Compétences",
      certifications: "Certifications",
      proficiencyLevels: "Niveau de compétence",
      filterByLabel: "Filtrer par {label}",
      clearAll: "Tout effacer",
      clear: "Effacer",
      emptyWorkerTypes: "Aucun type de travailleur dans les données actuelles",
      emptyJobFunctions: "Aucune fonction dans les données actuelles",
      emptySkills: "Aucune compétence dans les données actuelles",
      emptyCertifications: "Aucune certification dans les données actuelles",
      emptyProficiencyLevels: "Aucun niveau de compétence dans les données actuelles",
      chipName: "Nom",
      chipWorkerType: "Type de travailleur",
      chipJobFunction: "Fonction",
      chipSkill: "Compétence",
      chipCertification: "Certification",
      removeFilterAria: "Retirer le filtre {label}",
    },
    filterPane: {
      title: "Filtres",
      description: "Filtrer le tableau de bord par attributs de travailleur. Les filtres s'appliquent immédiatement.",
      triggerLabel: "Filtres",
      activeCountAria: "{n} filtres actifs",
      resetAll: "Tout réinitialiser",
      searchSection: "Recherche",
      workerTypeSection: "Type de travailleur",
      jobFunctionSection: "Fonction",
      skillsSection: "Compétences",
      certificationsSection: "Certifications",
      proficiencyLevelsSection: "Niveau de compétence",
      branchSection: "Succursale",
      workerLocationSection: "Lieu du travailleur",
      procurementSourceSection: "Source d'approvisionnement",
      functionalManagerSection: "Gestionnaire fonctionnel",
      engagementSection: "Engagement",
      emptyBranches: "Aucune succursale dans les données actuelles",
      emptyWorkerLocations: "Aucun lieu de travailleur dans les données actuelles",
      emptyProcurementSources: "Aucune source d'approvisionnement dans les données actuelles",
      emptyFunctionalManagers: "Aucun gestionnaire fonctionnel dans les données actuelles",
      emptyEngagements: "Aucun engagement dans les données actuelles",
      clearSection: "Effacer",
      sectionSearchPlaceholder: "Rechercher {title}",
      sectionSearchAria: "Rechercher dans les options {title}",
      clearSectionSearchAria: "Effacer la recherche",
      sectionNoMatches: "Aucune correspondance",
      activeFiltersLabel: "Filtres actifs",
      clearAllPills: "Tout effacer",
      removePillAria: "Retirer le filtre {label}",
    },
    heatmap: {
      workerHeader: "Travailleur",
      nowLabel: "AUJ.",
      emptyState: "Aucun travailleur ne correspond aux filtres actuels.",
      activeEngagementSubline: "{n} engagement actif",
      activeEngagementSublinePlural: "{n} engagements actifs",
      workerRoleAndActive: "{role} · {n} actif",
      moreSuffix: "+ {n} de plus",
      viewDetailAria: "Voir le détail de disponibilité pour {name}",
      overTooltip: "Les heures budgétées d'une journée dépassent la capacité de ce mois",
      overLabel: "surchargé",
      teamSelectionAria: "Ajouter {name} à la chronologie de l'équipe",
      selectAllAria: "Sélectionner tous les travailleurs",
      viewRangeFromLabel: "Du",
      viewRangeToLabel: "Au",
      viewRangeFromAria: "Filtrer la carte à partir du",
      viewRangeToAria: "Filtrer la carte jusqu'au",
      pendingTooltip: "Chargement…",
      timeOffHeading: "Absences",
      timeOffPtoLabel: "Congé",
      timeOffStatLabel: "Jour férié",
      timeOffHalfDaySuffix: "½ journée",
      timeOffIconAria: "Absences durant cette période",
      endsOnLabel: "Fin le {date}",
      endedOnLabel: "Terminé le {date}",
      endBucketTooltip: "Date de fin : {date}",
      afterEndTooltip: "Après la date de fin du travailleur ({date}) — aucune capacité à planifier",
      assignedPastEndTooltip: "Travail assigné après la date de fin du travailleur ({date}) — prolongez le profil ou déplacez le travail",
      expiredCertificationBadge: "Certification expirée",
      infoTitle: "Fonctionnement de la carte",
      allocationLabel: "Allocation",
      availabilityLabel: "Disponibilité",
      infoAllocated: "Heures d'engagement planifiées ÷ capacité.",
      infoAvailable: "(Capacité − heures planifiées − congés − jours fériés) ÷ capacité.",
      infoPtoBlind: "L'allocation ne tient pas compte des congés ni des jours fériés; l'allocation et la disponibilité ne totalisent donc pas 100 %.",
      infoCapacity: "La capacité ne compte que les jours ouvrables (lun.–ven.); les fins de semaine ne comptent jamais.",
      infoWeekly: "L'allocation et la disponibilité d'une semaine sont calculées sur l'ensemble de la semaine. Ainsi, un engagement à 8 heures par jour peut afficher 100 % d'allocation pour une semaine qu'il couvre entièrement, mais moins de 100 % pour sa première semaine s'il a débuté en milieu de semaine.",
      infoPalm: "Ce palmier signale les congés ou jours fériés dans une cellule. Survolez-le pour voir les dates exactes.",
      infoEndDate: "Ce drapeau marque la date de fin du travailleur : sa date de cessation d'emploi si une cessation est en attente, sinon la date de fin de son profil. Les cellules suivantes sont grisées. Un pourcentage rouge sur une cellule grisée signifie que du travail est déjà assigné après cette date. Les travailleurs salariés n'ont pas de date de fin de profil; leur ligne ne porte donc aucun drapeau, sauf si une cessation d'emploi est en attente.",
    },
    monthFilter: {
      filterAllocSubtitle: "Filtrer par % d'allocation",
      filterAvailSubtitle: "Filtrer par % de disponibilité",
      filterAllocAriaLabel: "Filtrer l'allocation de {month}",
      filterAvailAriaLabel: "Filtrer la disponibilité de {month}",
      opAbove: "≥ supérieur",
      opBelow: "≤ inférieur",
      valuePlaceholder: "ex. 75",
      apply: "Appliquer",
      clear: "Effacer",
    },
    workerActions: {
      triggerAriaLabel: "Actions pour {name}",
      showBreakdown: "Afficher la répartition des engagements",
      openProfileNewTab: "Ouvrir le profil dans un nouvel onglet",
    },
    drilldown: {
      backToTeam: "Retour à l'équipe",
      loading: "Chargement…",
      notFound: "Travailleur introuvable dans la fenêtre actuelle.",
      engagementSingular: "engagement",
      engagementPlural: "engagements",
      currentAllocated: "Allocation actuelle",
      currentSubtext: "Ce mois-ci (réel)",
      avgForecastAlloc: "Allocation prévue moyenne",
      avgSubtext: "12 prochains mois",
      fullAvailabilityStarts: "Pleine disponibilité dès le",
      nowLabel: "Maintenant",
      fullAvailabilityNoEng: "Aucun engagement actif",
      fullAvailabilitySubtext: "Après la fin de tous les engagements actuels",
      plannedPto: "Congés planifiés",
      plannedPtoDaysSingular: "{n} jour sur 12 mois",
      plannedPtoDaysPlural: "{n} jours sur 12 mois",
      expiredCertification: "Expirée",
      engagementTimeline: "Chronologie des engagements",
      timelineEmpty: "Aucun engagement actif",
      breakdownTitle: "Répartition de l'allocation — prévision sur 12 mois",
      monthlyForecast: "Prévision mensuelle",
      ptoUnavailable: "Données de congés indisponibles — les congés planifiés ne sont pas affichés.",
    },
    monthlyTable: {
      month: "Mois",
      capacity: "Capacité",
      allocated: "Alloué",
      stat: "Férié",
      pto: "Congés",
      allocPct: "% alloué",
      availPct: "% disponible",
    },
    timeline: {
      engagementHeader: "Engagement",
      endingSoonBadge: "fin proche",
      monthSingular: "1 mois",
      monthPlural: "{n} mois",
      workerActiveLegend: "Travailleur actif",
      engagementWindowLegend: "Fenêtre d'engagement",
      colorsVaryNote: "les couleurs varient selon le travailleur",
    },
    team: {
      fullyAvailable: "Entièrement disponible",
      engagementSingular: "engagement",
      engagementPlural: "engagements",
      removeWorkerAria: "Retirer {name} de la chronologie d'équipe",
      openEngagementAria: "Ouvrir l'engagement {name}",
      teamTimelineHeadingSingular: "Chronologie d'équipe · 1 travailleur",
      teamTimelineHeadingPlural: "Chronologie d'équipe · {n} travailleurs",
      clearSelection: "Effacer la sélection",
      viewRangeFromAria: "Filtrer la chronologie d'équipe à partir de la date",
      viewRangeToAria: "Filtrer la chronologie d'équipe jusqu'à la date",
    },
    breakdown: {
      allocated: "Alloué",
      available: "Disponible",
      pto: "Congés",
      statHoliday: "Jour férié",
    },
  },
}

// Dictionary map
export const dictionaries = {
  "en-CA": enCA,
  "fr-CA": frCA,
}

export type Dictionary = typeof enCA
export type Locale = keyof typeof dictionaries
